import { type IntlShape } from 'react-intl-next';

import { fg } from '@atlaskit/platform-feature-flags';
import { ConfluenceSpaceAri } from '@atlassian/ari';

import {
	type ConfluenceSearchPageResult,
	type ConfluenceSearchResultWithLinks,
	searchConfluencePage,
	searchConfluencePages,
} from '../../../../services/fetch-page';
import {
	type ConfluenceSearchSpaceResult,
	type CrossProductSearchSpaceResult,
	type CrossProductSearchSpaceResultV2,
	searchConfluenceSpace,
	searchConfluenceSpaces,
	searchConfluenceSpacesV2,
} from '../../../../services/fetch-space';
import { removeTrailingSlash } from '../../../../services/utils';

import { messages } from './messages';

export type PageSearchResult = Pick<ConfluenceSearchResultWithLinks, 'results' | '_links'>;

export type SpaceOption = {
	label: string;
	value: {
		spaceId: string;
		spaceKey: string;
		spaceName: string;
		spaceUrl: string;
		iconPath: string;
		isPage: false;
		isLivePagesEnabled?: boolean;
	};
};

export type PageOption = {
	label: string;
	value: {
		spaceId: string;
		spaceUrl: string;
		spaceKey: string;
		spaceName: string;
		linkedPageId: string;
		linkedPageTitle: string;
		linkedPageUrl: string;
		isPage: true;
		isLive?: boolean;
		isLivePagesEnabledForParentSpace?: boolean;
	};
};

export type MergedOptions = [
	{
		label: string;
		options: SpaceOption[];
	},
	{
		label: string;
		options: PageOption[];
	},
];

const mapPageOptions = (pageSearch: PageSearchResult): PageOption[] => {
	return pageSearch?.results?.map(({ title, space, id, _links, subType }) => ({
		label: title,
		value: {
			spaceId: `${space.id}`,
			spaceKey: space.key,
			spaceName: space.name,
			linkedPageId: id,
			linkedPageTitle: title,
			isPage: true,
			isLive: subType === 'live',
			spaceUrl: `${pageSearch._links.base}/spaces/${space.key}`,
			linkedPageUrl: `${pageSearch._links.base}${_links?.webui}`,
			isLivePagesEnabledForParentSpace: space.typeSettings?.enabledContentTypes?.isLivePagesEnabled,
		},
	}));
};

const mapSpaceOptions = (spaceSearch: CrossProductSearchSpaceResultV2): SpaceOption[] => {
	return spaceSearch?.data?.search?.search?.edges?.map(({ node }) => ({
		label: node.title,
		value: {
			spaceId: ConfluenceSpaceAri.parse(node.spaceEntity.id).spaceId,
			spaceKey: node.spaceEntity.key,
			spaceName: node.title,
			spaceUrl: `${node.spaceEntity.links.base}${node.spaceEntity.links.webUi}`,
			iconPath: `${node.spaceEntity.links.base}${node.spaceEntity.icon.path}`,
			isPage: false,
			isLivePagesEnabled: node.spaceEntity.typeSettings.enabledContentTypes.isLivePagesEnabled,
		},
	}));
};

/*
 *  Remove when cleaning up link-create-search-confluence-spaces-gql feature gate, use mapPageAndSpaceOptionsV2 instead
 */
const mapPageAndSpaceOptions = (
	spaceSearch: CrossProductSearchSpaceResult,
	pageSearch: PageSearchResult,
	intl: IntlShape,
): MergedOptions => {
	const spaceOptions: SpaceOption[] = spaceSearch?.scopes?.[0]?.results?.map(
		({ title, space, baseUrl, url, id }) => ({
			label: title,
			value: {
				spaceId: id,
				spaceKey: space?.key,
				spaceName: title,
				spaceUrl: `${baseUrl}${url}`,
				iconPath: `${baseUrl}${space?.icon?.path}`,
				isPage: false,
			},
		}),
	);

	const pageOptions: PageOption[] = mapPageOptions(pageSearch);

	return [
		{
			label: intl.formatMessage(messages.spacesOptionLabel),
			options: spaceOptions,
		},
		{
			label: intl.formatMessage(messages.contentOptionLabel),
			options: pageOptions,
		},
	];
};

const mapPageAndSpaceOptionsV2 = (
	spaceSearch: CrossProductSearchSpaceResultV2,
	pageSearch: PageSearchResult,
	intl: IntlShape,
	filterOptionsForLiveDocs?: boolean,
): MergedOptions => {
	const spaceOptions: SpaceOption[] = mapSpaceOptions(spaceSearch);
	const pageOptions: PageOption[] = mapPageOptions(pageSearch);

	return [
		{
			label: intl.formatMessage(messages.spacesOptionLabel),
			options:
				filterOptionsForLiveDocs && fg('live_docs_button_issue_view_improvements')
					? filterSpacesForLiveDocs(spaceOptions)
					: spaceOptions,
		},
		{
			label: intl.formatMessage(messages.contentOptionLabel),
			options:
				filterOptionsForLiveDocs && fg('live_docs_button_issue_view_improvements')
					? filterParentPagesForLiveDocs(pageOptions)
					: pageOptions,
		},
	];
};

const SPACES_MAX_RESULTS = 5;

export const fetchPageAndSpaceOptions = async (
	inputValue: string,
	cloudId: string,
	gatewayBaseUrl: string = '',
	intl: IntlShape,
	filterOptionsForLiveDocs?: boolean,
) => {
	const pageSearch: PageSearchResult = await searchConfluencePages({
		searchString: inputValue,
		cloudId,
		gatewayBaseUrl,
	});

	let options;
	if (fg('link-create-search-confluence-spaces-gql')) {
		let spaceSearch: CrossProductSearchSpaceResultV2 = await searchConfluenceSpacesV2({
			cloudId,
			limit: SPACES_MAX_RESULTS, // Limit displayed spaces to give room for pages results
			searchString: inputValue,
			gatewayBaseUrl,
		});
		options = mapPageAndSpaceOptionsV2(spaceSearch, pageSearch, intl, filterOptionsForLiveDocs);
	} else {
		let spaceSearch: CrossProductSearchSpaceResult = await searchConfluenceSpaces({
			cloudId,
			limit: SPACES_MAX_RESULTS, // Limit displayed spaces to give room for pages results
			searchString: inputValue,
			gatewayBaseUrl,
		});
		options = mapPageAndSpaceOptions(spaceSearch, pageSearch, intl);
	}

	return options;
};

export const mapDefaultValues = (
	intl: IntlShape,
	spaceSearch?: ConfluenceSearchSpaceResult,
	pageSearch?: ConfluenceSearchPageResult,
	gatewayBaseUrl?: string,
) => {
	const spaceOption: SpaceOption | undefined = spaceSearch && {
		label: spaceSearch.name,
		value: {
			isPage: false,
			spaceId: spaceSearch.id,
			spaceKey: spaceSearch.key,
			spaceName: spaceSearch.name,
			iconPath: `${removeTrailingSlash(gatewayBaseUrl)}${spaceSearch.icon?.path}`,
			spaceUrl: `${removeTrailingSlash(gatewayBaseUrl)}${spaceSearch._links?.webui}`,
		},
	};

	const isPageAndSpaceRelated = pageSearch?.spaceId === spaceSearch?.id;

	const pageOption: PageOption | undefined = pageSearch && {
		label: pageSearch.title || intl.formatMessage(messages.pageUntitledDraft),
		value: {
			isPage: true,
			isLive: pageSearch.subtype === 'live',
			spaceId: pageSearch.spaceId,
			spaceKey: isPageAndSpaceRelated && spaceSearch ? spaceSearch.key : '',
			spaceName: isPageAndSpaceRelated && spaceSearch ? spaceSearch.name : '',
			linkedPageId: pageSearch.id,
			linkedPageTitle: pageSearch.title,
			spaceUrl:
				isPageAndSpaceRelated && spaceSearch
					? `${removeTrailingSlash(gatewayBaseUrl)}${spaceSearch?._links?.webui}`
					: '',
			linkedPageUrl: '',
		},
	};

	/**
	 * returns pageOption first as it has a higher specificity than spaceOption
	 */
	return pageOption ?? spaceOption;
};

export const fetchDefaultValues = async (
	intl: IntlShape,
	cloudId: string,
	gatewayBaseUrl: string = '',
	defaultSpaceId?: string,
	defaultParentId?: string,
	onFailure?: (error: unknown) => void,
) => {
	let pageSearch;
	let spaceSearch;

	if (defaultSpaceId) {
		spaceSearch = searchConfluenceSpace({
			cloudId,
			gatewayBaseUrl,
			defaultSpaceId,
		});
	}

	if (defaultParentId) {
		pageSearch = searchConfluencePage({
			cloudId,
			gatewayBaseUrl,
			defaultParentId,
		});
	}

	const responses = await Promise.allSettled([spaceSearch, pageSearch]);

	responses.forEach((res) => {
		if (res.status === 'rejected') {
			onFailure?.(res.reason);
		}
	});

	const [page, space] = responses;

	return mapDefaultValues(
		intl,
		page.status === 'fulfilled' ? page.value : undefined,
		space.status === 'fulfilled' ? space.value : undefined,
		gatewayBaseUrl,
	);
};

/**
 * Customers should only be able to create live docs in spaces which have live docs enabled.
 * This function filters out any spaces which do not have live docs enabled.
 */
const filterSpacesForLiveDocs = (options: SpaceOption[]) => {
	return options.filter((option) => option.value.isLivePagesEnabled);
};

/**
 * Customers should only be able to create live docs in spaces which have live docs enabled.
 * This function filters out any pages which are located in spaces without live docs enabled.
 */
const filterParentPagesForLiveDocs = (options: PageOption[]) => {
	return options.filter((option) => option.value.isLivePagesEnabledForParentSpace);
};
